/**
 * Central React Query key factory.
 *
 * Every query key is namespaced by its feature domain so keys never collide
 * and mutations can invalidate at the right granularity. List keys carry their
 * filter object as the final segment, so changing a filter transparently
 * refetches (and caches) a distinct result set. React Query hashes keys
 * structurally (deterministic, sorted-key JSON), so passing a filter object by
 * value is safe — object identity does not matter.
 *
 * Two sanctioned key shapes — pick per domain and stay consistent:
 *
 *   (a) Single-collection domain (one primary list + items), e.g. `projects`:
 *         all                 -> ["projects"]
 *         lists()             -> ["projects", "list"]
 *         list(filters)       -> ["projects", "list", filters]
 *         detail(id)          -> ["projects", "detail", id]
 *
 *   (b) Multi-collection domain (several named sub-resources under one root),
 *       e.g. `marketplace`, `catalog`, `realtor`:
 *         all                 -> ["marketplace"]
 *         designers(filters)  -> ["marketplace", "designers", filters]
 *         designer(id)        -> ["marketplace", "designer", id]
 *
 * `filters` is optional and typed as a serializable record — invalidate a whole
 * collection with `lists()` / `all`, or a filtered slice with `list(filters)`.
 *
 * Each feature extends its own namespace as it migrates to React Query.
 */

type Filters = Record<string, unknown>;

export const queryKeys = {
	// features/auth, features/settings — current user + profiles
	user: {
		all: ["user"] as const,
		me: () => [...queryKeys.user.all, "me"] as const,
		roles: () => [...queryKeys.user.all, "roles"] as const,
		// Keyed by the active role: the server leaves that role's own profile out.
		onboardingPrefill: (role: string | null) =>
			[...queryKeys.user.all, "onboarding-prefill", role] as const,
	},

	settings: {
		all: ["settings"] as const,
		homeownerProfile: () =>
			[...queryKeys.settings.all, "homeowner-profile"] as const,
		// The signed-in user's OWN designer/contractor profile (no id). Distinct
		// read model from marketplace.designer(id)/contractor(id), which key
		// another user's PUBLIC profile browsed by id.
		designerProfile: () =>
			[...queryKeys.settings.all, "designer-profile"] as const,
		contractorProfile: () =>
			[...queryKeys.settings.all, "contractor-profile"] as const,
		realtorProfile: () =>
			[...queryKeys.settings.all, "realtor-profile"] as const,
		// Canonical key for the current homeowner's properties. Onboarding, which
		// creates properties, invalidates this same key rather than owning a
		// separate one, so post-create reads never go stale.
		properties: () => [...queryKeys.settings.all, "properties"] as const,
	},

	notifications: {
		all: ["notifications"] as const,
		lists: () => [...queryKeys.notifications.all, "list"] as const,
		list: (filters?: Filters) =>
			[...queryKeys.notifications.lists(), filters ?? {}] as const,
	},

	projects: {
		all: ["projects"] as const,
		lists: () => [...queryKeys.projects.all, "list"] as const,
		list: (filters?: Filters) =>
			[...queryKeys.projects.lists(), filters ?? {}] as const,
		detail: (id: string) => [...queryKeys.projects.all, "detail", id] as const,
		proposals: (projectId: string) =>
			[...queryKeys.projects.detail(projectId), "proposals"] as const,
		requests: (projectId: string) =>
			[...queryKeys.projects.detail(projectId), "requests"] as const,
		tasks: (projectId: string) =>
			[...queryKeys.projects.detail(projectId), "tasks"] as const,
		milestones: (projectId: string) =>
			[...queryKeys.projects.detail(projectId), "milestones"] as const,
		costs: (projectId: string) =>
			[...queryKeys.projects.detail(projectId), "costs"] as const,
		activity: (projectId: string) =>
			[...queryKeys.projects.detail(projectId), "activity"] as const,
		payments: (projectId: string) =>
			[...queryKeys.projects.detail(projectId), "payments"] as const,
		contractors: (projectId: string) =>
			[...queryKeys.projects.detail(projectId), "contractors"] as const,
		documents: (projectId: string) =>
			[...queryKeys.projects.detail(projectId), "documents"] as const,
		shortlist: (projectId: string) =>
			[...queryKeys.projects.detail(projectId), "shortlist"] as const,
		boq: (projectId: string) =>
			[...queryKeys.projects.detail(projectId), "boq"] as const,
		messages: (projectId: string) =>
			[...queryKeys.projects.detail(projectId), "messages"] as const,
		// Requests addressed to the signed-in designer across every project.
		// Distinct from `requests(projectId)`, which is one project's requests.
		designerRequests: () =>
			[...queryKeys.projects.all, "designer-requests"] as const,
		// How many proposals the signed-in designer has submitted, across every
		// project. Distinct from `proposals(projectId)`, which is one project's.
		designerProposalCount: () =>
			[...queryKeys.projects.all, "designer-proposal-count"] as const,
	},

	// The signed-in user's own escrow transactions (payer or payee side) across
	// every project. Project-scoped payments stay under `projects.payments`.
	payments: {
		all: ["payments"] as const,
		history: () => [...queryKeys.payments.all, "history"] as const,
	},

	reviews: {
		all: ["reviews"] as const,
		forDesigner: (designerId: string) =>
			[...queryKeys.reviews.all, "designer", designerId] as const,
		projectEligibility: (projectId: string) =>
			[...queryKeys.reviews.all, "project-eligibility", projectId] as const,
	},

	marketplace: {
		all: ["marketplace"] as const,
		designers: (filters?: Filters) =>
			[...queryKeys.marketplace.all, "designers", filters ?? {}] as const,
		designer: (id: string) =>
			[...queryKeys.marketplace.all, "designer", id] as const,
		contractors: (filters?: Filters) =>
			[...queryKeys.marketplace.all, "contractors", filters ?? {}] as const,
		contractor: (id: string) =>
			[...queryKeys.marketplace.all, "contractor", id] as const,
		opportunities: (filters?: Filters) =>
			[...queryKeys.marketplace.all, "opportunities", filters ?? {}] as const,
		assignments: (filters?: Filters) =>
			[...queryKeys.marketplace.all, "assignments", filters ?? {}] as const,
		invitation: (id: string) =>
			[...queryKeys.marketplace.all, "invitation", id] as const,
		publicProject: (id: string) =>
			[...queryKeys.marketplace.all, "public-project", id] as const,
	},

	catalog: {
		all: ["catalog"] as const,
		categories: () => [...queryKeys.catalog.all, "categories"] as const,
		brands: () => [...queryKeys.catalog.all, "brands"] as const,
		products: (filters?: Filters) =>
			[...queryKeys.catalog.all, "products", filters ?? {}] as const,
		product: (id: string) => [...queryKeys.catalog.all, "product", id] as const,
		compare: (ids: string[]) =>
			[...queryKeys.catalog.all, "compare", [...ids].sort()] as const,
	},

	dashboard: {
		all: ["dashboard"] as const,
		home: (userType: string) =>
			[...queryKeys.dashboard.all, "home", userType] as const,
	},

	// Current user's payout account (professionals). User-scoped, not
	// project-scoped, though the service lives in the projects/payments module.
	payouts: {
		all: ["payouts"] as const,
		me: () => [...queryKeys.payouts.all, "me"] as const,
	},

	// Current user's signed-in devices (trusted-device sessions).
	sessions: {
		all: ["sessions"] as const,
		list: () => [...queryKeys.sessions.all, "list"] as const,
	},

	// Admin-editable trades / service-cities lists (issue #331). Rarely
	// change, so a long staleTime is set where these are fetched.
	config: {
		all: ["config"] as const,
		trades: () => [...queryKeys.config.all, "trades"] as const,
		cities: () => [...queryKeys.config.all, "cities"] as const,
	},

	comments: {
		all: ["comments"] as const,
		// Comments attached to a parent resource (e.g. a project). Named
		// distinctly from the (filters)-style `list` in single-collection
		// domains to avoid signature confusion.
		forResource: (resource: string, resourceId: string) =>
			[...queryKeys.comments.all, resource, resourceId] as const,
	},

	portfolio: {
		all: ["portfolio"] as const,
		detail: (id: string) => [...queryKeys.portfolio.all, "detail", id] as const,
	},

	contractorProfile: {
		all: ["contractor-profile"] as const,
		detail: (id: string) =>
			[...queryKeys.contractorProfile.all, "detail", id] as const,
	},

	sharing: {
		all: ["sharing"] as const,
		shareLink: (id: string) =>
			[...queryKeys.sharing.all, "share-link", id] as const,
		projectHistory: (projectId: string) =>
			[...queryKeys.sharing.all, "project-history", projectId] as const,
	},

	realtor: {
		all: ["realtor"] as const,
		listings: (filters?: Filters) =>
			[...queryKeys.realtor.all, "listings", filters ?? {}] as const,
		listing: (id: string) => [...queryKeys.realtor.all, "listing", id] as const,
		leads: (filters?: Filters) =>
			[...queryKeys.realtor.all, "leads", filters ?? {}] as const,
		handoffs: (filters?: Filters) =>
			[...queryKeys.realtor.all, "handoffs", filters ?? {}] as const,
		referrals: (filters?: Filters) =>
			[...queryKeys.realtor.all, "referrals", filters ?? {}] as const,
		apiKeys: () => [...queryKeys.realtor.all, "api-keys"] as const,
		// The morning follow-up email's settings and address.
		digestSettings: () =>
			[...queryKeys.realtor.all, "digest-settings"] as const,
		// The WhatsApp Business connection's status, read by settings and Home.
		whatsappConnection: () =>
			[...queryKeys.realtor.all, "whatsapp-connection"] as const,
		// The WhatsApp chats waiting to be saved as contacts or ignored, paged.
		whatsappReview: () =>
			[...queryKeys.realtor.all, "whatsapp-review"] as const,
		schedule: (filters?: Filters) =>
			[...queryKeys.realtor.all, "schedule", filters ?? {}] as const,
		contacts: (filters?: Filters) =>
			[...queryKeys.realtor.all, "contacts", filters ?? {}] as const,
		contact: (id: string) => [...queryKeys.realtor.all, "contact", id] as const,
		threads: () => [...queryKeys.realtor.all, "threads"] as const,
		// One person's merged messages and lead activities, paged.
		timelines: () => [...queryKeys.realtor.all, "timeline"] as const,
		timeline: (contactId: string) =>
			[...queryKeys.realtor.timelines(), contactId] as const,
		// The realtor's current follow-up nudges.
		followUps: () => [...queryKeys.realtor.all, "follow-ups"] as const,
		followUpList: () => [...queryKeys.realtor.followUps(), "list"] as const,
		enquiries: (filters?: Filters) =>
			[...queryKeys.realtor.all, "enquiries", filters ?? {}] as const,
		enquiry: (id: string) => [...queryKeys.realtor.all, "enquiry", id] as const,
	},
} as const;
